import CustomLabel from '@/common/components/base/customLabel/CustomLabel';
import CardButton from '@/common/components/cardButton/CardButton';
import MainLayout from '@/common/components/layouts/MainLayout';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box } from 'styled-system/jsx';

import AccidentInfoIcon from '@/common/assets/icons/accidentInfoIcon.svg?react';
import AccidentPersonInfoIcon from '@/common/assets/icons/accidentPersonInfoIcon.svg?react';
import AdjusterOpinionIcon from '@/common/assets/icons/adjusterOpinionIcon.svg?react';
import PoliceReport from '@/common/assets/icons/policeReport.svg?react';
import VisitInformationIcon from '@/common/assets/icons/visitInformationIcon.svg?react';
import CustomIcon from '@/common/components/base/customIcon/CustomIcon';
import LoadingPage from '@/common/pages/LoadingPage';
import { FileTypeConst } from '@/common/types/Files.type';
import { ADJUSTER_REPORT_LINKS } from '@/modules/adjusterReportModules/adjusterReport/AdjusterReportLinks';
import { ADJUSTER_REPORT_BODY_DAMAGE_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/bodyDamage/bodyDamageLinks';
import { ADJUSTER_REPORT_CULPRIT_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/culprit/culpritLinks';
import { ADJUSTER_REPORT_DAMAGED_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/damaged/damagedLinks';
import { AdjusterReportQueryResponseType } from '@/modules/adjusterReportModules/adjusterReport/queries/adjusterReportPage/adjusterReportQuery.type';
import { adjusterReportTabsQueryOptions } from '@/modules/adjusterReportModules/adjusterReport/queries/adjusterReportPage/adjusterReportQueryOptions';
import useVehicleTypeMiss from '@/modules/adjusterReportModules/hooks/useVehicleTypeMiss';
import { IncidentDetailsLinks } from '@/modules/adjusterReportModules/IncidentDetails/IncidentDetailsLinks';
import VehicleTypeMissErrorModal from '@/modules/adjusterReportModules/IncidentParticipants/components/modals/VehicleTypeMissErrorModal';
import { PARTICIPANTS_INCIDENT_LINKS } from '@/modules/adjusterReportModules/IncidentParticipants/Links';
import { fileEntryQueryOptions } from '@/modules/FileDetail/queries/fileEntry/fileEntryQueryOptions';
import React from 'react';
import GalleryIcon from '@/common/assets/icons/galleryIcon.svg?react';
import { token } from 'styled-system/tokens';

export default () => (
  <MainLayout header="titleHeader" title="گزارش کارشناس">
    <AdjusterReportPage />
  </MainLayout>
);

const AdjusterReportPage = () => {
  const { id } = useParams();
  const {
    data: entryTabsData,
    isError,
    isPending,
  } = useQuery(adjusterReportTabsQueryOptions(id!));

  const { data: fileData, isPending: fileDetailIsPending } = useQuery(
    fileEntryQueryOptions(id!),
  );
  const {
    error,
    modalState: {
      isVehicleTypeErrorModalOpen,
      vehicleTypeErrorModalCloseModal,
      vehicleTypeErrorModalOpenModal,
    },
    loading,
    route,
  } = useVehicleTypeMiss({ fileId: id! });
  const navigate = useNavigate();
  if (isError) {
    return <></>;
  }
  if (isPending || fileDetailIsPending || loading) {
    return <LoadingPage />;
  }

  const renderRoute = (item: AdjusterReportQueryResponseType) => {
    const filetype: FileTypeConst = fileData?.file_type?.const!;
    const editable = item.editable;
    console.log(item);

    const routes = {
      adjusterOpinion: editable
        ? ADJUSTER_REPORT_LINKS.adjusterOpinion.edit(id!)
        : ADJUSTER_REPORT_LINKS.adjusterOpinion.show(id!),

      culprit_documents: editable
        ? ADJUSTER_REPORT_LINKS.adjusterOpinion.edit(id!)
        : ADJUSTER_REPORT_LINKS.adjusterOpinion.show(id!),
      damages_documents: editable
        ? ADJUSTER_REPORT_LINKS.adjusterOpinion.edit(id!)
        : ADJUSTER_REPORT_LINKS.adjusterOpinion.show(id!),
      policeReport: editable
        ? ADJUSTER_REPORT_LINKS.policeReport.edit(id!)
        : ADJUSTER_REPORT_LINKS.policeReport.show(id!),

      propertyIncident: editable
        ? IncidentDetailsLinks.INCIDENT_DETAIL_PROPERTY_BASE_PATH(id!)
        : IncidentDetailsLinks.INCIDENT_SHOW_DETAIL_PROPERTY(id!),

      bodyIncident: editable
        ? IncidentDetailsLinks.INCIDENT_DETAIL_BODY_BASE_PATH(id!)
        : IncidentDetailsLinks.INCIDENT_SHOW_DETAIL_BODY_BASE_PATH(id!),

      initialVisit: editable
        ? IncidentDetailsLinks.INCIDENT_DETAIL_INITIAL_VISIT_BASE_PATH(id!)
        : IncidentDetailsLinks.INCIDENT_SHOW_DETAIL_INITIAL_VISIT_BASE_PATH(
            id!,
          ),
    };

    const obj: Record<FileTypeConst, any> = {
      property: {
        accident_information: routes.propertyIncident,
        culprit_information: ADJUSTER_REPORT_CULPRIT_LINKS.CULPRIT_INFORMATION({
          id: +id!,
        }).culpritInformationTabsPage,
        culprit_documents: ADJUSTER_REPORT_CULPRIT_LINKS.CULPRIT_IMAGES({
          id: +id!,
        }).imagesTabPage,
        damages_information:
          item.count && item.count > 1
            ? ADJUSTER_REPORT_DAMAGED_LINKS.damagedListInformation({ id: id! })
            : ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
                damagedId: item.damaged_id,
                id: id!,
              }).damagedInformationTabsPage,
        damages_documents:
          item.count && item.count > 1
            ? ADJUSTER_REPORT_DAMAGED_LINKS.damagedListImages({ id: id! })
            : ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_IMAGES({
                damagedId: item.damaged_id,
                id: id!,
              }).imagesTabPage,

        adjuster_opinion: routes.adjusterOpinion,
        police_report: routes.policeReport,
      },
      body: {
        police_report: routes.policeReport,
        adjuster_opinion: routes.adjusterOpinion,
        insurer_information:
          ADJUSTER_REPORT_BODY_DAMAGE_LINKS.BODY_DAMAGE_INFORMATION({
            id: id!,
          }).bodyDamageInformationTabsPage,
        insurer_images: ADJUSTER_REPORT_BODY_DAMAGE_LINKS.BODY_DAMAGE_IMAGES({
          id: id!,
        }).imagesTabPage,
        accident_information: routes.bodyIncident,
      },
      initial: {
        adjuster_opinion: routes.adjusterOpinion,
        visit_information: routes.initialVisit,
        insurer_information: PARTICIPANTS_INCIDENT_LINKS.insurance_information(
          id!,
        ),
      },
    };

    return obj[filetype][item.name];
  };

  const goToFile = (item: AdjusterReportQueryResponseType) => {
    if (
      error &&
      item.editable &&
      (item.name === 'culprit_documents' || item.name === 'damages_documents')
    ) {
      vehicleTypeErrorModalOpenModal(renderRoute(item));
      return;
    }

    navigate(renderRoute(item));
  };
  return (
    <Box pt="2rem">
      {entryTabsData.map((item) => (
        <CardButton
          key={item.name}
          cardProps={{
            className: css({ marginY: '1rem' }),
            onClick: () => goToFile(item),
          }}
          hasArrow
          renderTag={() => (
            <CustomLabel
              title={item.label.fa_const}
              color={item.label.const === 'completed' ? 'green' : 'yellow'}
            />
          )}
          renderIcon={() => renderIcon(item.name)}
          title={item.fa_name}
        />
      ))}
      <VehicleTypeMissErrorModal
        open={isVehicleTypeErrorModalOpen}
        closeModal={vehicleTypeErrorModalCloseModal}
        onNavigate={() => {
          navigate(route);
        }}
      />
    </Box>
  );
};

const renderIcon = (tabName: AdjusterReportQueryResponseType['name']) => {
  let tabIcon: Record<
    AdjusterReportQueryResponseType['name'],
    React.JSX.Element
  > = {
    accident_information: <AccidentInfoIcon />,
    culprit_information: (
      <AccidentPersonInfoIcon
        fill={token('colors.orange')}
        color={token('colors.orange-3')}
      />
    ),
    culprit_documents: (
      <CustomIcon backgroundColor="#FEF3E9" width={40} height={40}>
        <GalleryIcon color={token('colors.orange')} />
      </CustomIcon>
    ),
    adjuster_opinion: <AdjusterOpinionIcon />,
    police_report: <PoliceReport />,
    damages_documents: (
      <CustomIcon backgroundColor="green-4" width={40} height={40}>
        <GalleryIcon color={token('colors.green-2')} />
      </CustomIcon>
    ),
    damages_information: (
      <AccidentPersonInfoIcon
        fill={token('colors.green-2')}
        color={token('colors.green-4')}
      />
    ),
    insurer_information: (
      <AccidentPersonInfoIcon
        fill={token('colors.orange')}
        color={token('colors.orange-3')}
      />
    ),
    insurer_images: (
      <CustomIcon backgroundColor="green-4" width={40} height={40}>
        <GalleryIcon color={token('colors.green-2')} />
      </CustomIcon>
    ),
    visit_information: (
      <CustomIcon backgroundColor="#E9FEF5" width={40} height={40}>
        <VisitInformationIcon />
      </CustomIcon>
    ),
  };

  return tabIcon[tabName];
};
